import {
    createDefaultCvDraft,
    buildDefaultExperience,
    buildDefaultMission,
} from "@/lib/cvDraftContract";
import { clearLocalDraft } from "@/stores/cvBuilder/autosave";

const markUserEdited = (draft, path) => ({
    ...draft,
    source_metadata: {
        ...(draft.source_metadata || {}),
        field_provenance: {
            ...(draft.source_metadata?.field_provenance || {}),
            [path]: "USER_EDITED",
        },
    },
});

export const createCandidateActions = (setDraft) => ({
    setCandidateField: (field, value) => {
        setDraft((prev) => {
            const nextCandidate = { ...prev.candidate, [field]: value };
            const nextSourceMeta = { ...(prev.source_metadata || {}) };
            const nextProvenance = { ...(nextSourceMeta.field_provenance || {}) };
            nextProvenance[`candidate.${field}`] = "USER_EDITED";
            nextSourceMeta.field_provenance = nextProvenance;
            return {
                ...prev,
                candidate: nextCandidate,
                source_metadata: nextSourceMeta,
            };
        });
    },
    resetDraft: () => {
        setDraft(createDefaultCvDraft());
        clearLocalDraft();
    },
});

export const createSkillsActions = (setDraft) => ({
    setSkillsField: (field, value) => {
        setDraft((prev) => {
            const nextSkills = { ...prev.skills, [field]: value };
            const nextSourceMeta = { ...(prev.source_metadata || {}) };
            const nextProvenance = { ...(nextSourceMeta.field_provenance || {}) };
            nextProvenance[`skills.${field}`] = "USER_EDITED";
            nextSourceMeta.field_provenance = nextProvenance;
            return {
                ...prev,
                skills: nextSkills,
                source_metadata: nextSourceMeta,
            };
        });
    },
    addSkillCategory: () => {
        setDraft((prev) => markUserEdited({
            ...prev,
            skills: {
                ...prev.skills,
                categories: [...prev.skills.categories, { name: "", items: [] }],
            },
        }, "skills.categories"));
    },
    updateSkillCategory: (index, category) => {
        setDraft((prev) => {
            const nextCategories = [...prev.skills.categories];
            nextCategories[index] = category;
            return markUserEdited({
                ...prev,
                skills: {
                    ...prev.skills,
                    categories: nextCategories,
                },
            }, `skills.categories.${index}`);
        });
    },
    removeSkillCategory: (index) => {
        setDraft((prev) => markUserEdited({
            ...prev,
            skills: {
                ...prev.skills,
                categories: prev.skills.categories.filter((_, i) => i !== index),
            },
        }, "skills.categories"));
    },
});

export const createExperienceActions = (setDraft) => ({
    addExperience: () => {
        setDraft((prev) => markUserEdited({
            ...prev,
            experiences: [
                buildDefaultExperience(),
                ...prev.experiences,
            ],
        }, "experiences"));
    },
    updateExperience: (index, experience) => {
        setDraft((prev) => {
            const nextExperiences = [...prev.experiences];
            nextExperiences[index] = experience;
            return markUserEdited({
                ...prev,
                experiences: nextExperiences,
            }, `experiences.${index}`);
        });
    },
    removeExperience: (index) => {
        setDraft((prev) => markUserEdited({
            ...prev,
            experiences: prev.experiences.filter((_, i) => i !== index),
        }, "experiences"));
    },
    moveExperience: (fromIndex, toIndex) => {
        setDraft((prev) => {
            if (toIndex < 0 || toIndex >= prev.experiences.length || fromIndex === toIndex) return prev;
            const nextExperiences = [...prev.experiences];
            const [moved] = nextExperiences.splice(fromIndex, 1);
            nextExperiences.splice(toIndex, 0, moved);
            return markUserEdited({ ...prev, experiences: nextExperiences }, "experiences.order");
        });
    },
    addMission: (expIndex) => {
        setDraft((prev) => {
            const nextExperiences = [...prev.experiences];
            const missions = [
                ...(nextExperiences[expIndex].missions || []),
                { ...buildDefaultMission() },
            ];
            nextExperiences[expIndex] = {
                ...nextExperiences[expIndex],
                missions,
                projects: missions,
            };
            return markUserEdited({
                ...prev,
                experiences: nextExperiences,
            }, `experiences.${expIndex}.missions`);
        });
    },
    updateMission: (expIndex, missionIndex, mission) => {
        setDraft((prev) => {
            const nextExperiences = [...prev.experiences];
            const nextMissions = [...nextExperiences[expIndex].missions];
            nextMissions[missionIndex] = mission;
            nextExperiences[expIndex] = {
                ...nextExperiences[expIndex],
                missions: nextMissions,
                projects: nextMissions,
            };
            return markUserEdited({
                ...prev,
                experiences: nextExperiences,
            }, `experiences.${expIndex}.missions.${missionIndex}`);
        });
    },
    removeMission: (expIndex, missionIndex) => {
        setDraft((prev) => {
            const nextExperiences = [...prev.experiences];
            const missions = nextExperiences[expIndex].missions.filter((_, i) => i !== missionIndex);
            nextExperiences[expIndex] = {
                ...nextExperiences[expIndex],
                missions,
                projects: missions,
            };
            return markUserEdited({
                ...prev,
                experiences: nextExperiences,
            }, `experiences.${expIndex}.missions`);
        });
    },
    moveMission: (expIndex, fromIndex, toIndex) => {
        setDraft((prev) => {
            const nextExperiences = [...prev.experiences];
            const missions = [...(nextExperiences[expIndex].missions || [])];
            if (toIndex < 0 || toIndex >= missions.length || fromIndex === toIndex) return prev;
            const [moved] = missions.splice(fromIndex, 1);
            missions.splice(toIndex, 0, moved);
            nextExperiences[expIndex] = { ...nextExperiences[expIndex], missions, projects: missions };
            return markUserEdited({ ...prev, experiences: nextExperiences }, `experiences.${expIndex}.missions.order`);
        });
    },
});

export const createProfileActions = (setDraft) => ({
    setProfileField: (field, value) => {
        if (!['projects', 'additional_sections'].includes(field) || !Array.isArray(value)) return;
        setDraft(prev => markUserEdited({ ...prev, [field]: value }, field));
    },
    addCertification: () => {
        setDraft((prev) => markUserEdited({
            ...prev,
            certifications: [...prev.certifications, { name: "", year: "", issuer: "" }],
        }, "certifications"));
    },
    updateCertification: (index, cert) => {
        setDraft((prev) => {
            const nextCerts = [...prev.certifications];
            nextCerts[index] = cert;
            return markUserEdited({
                ...prev,
                certifications: nextCerts,
            }, `certifications.${index}`);
        });
    },
    removeCertification: (index) => {
        setDraft((prev) => markUserEdited({
            ...prev,
            certifications: prev.certifications.filter((_, i) => i !== index),
        }, "certifications"));
    },
    addEducation: () => {
        setDraft((prev) => markUserEdited({
            ...prev,
            education: [...prev.education, { title: "", institution: "", year: "", details: "" }],
        }, "education"));
    },
    updateEducation: (index, edu) => {
        setDraft((prev) => {
            const nextEducation = [...prev.education];
            nextEducation[index] = edu;
            return markUserEdited({
                ...prev,
                education: nextEducation,
            }, `education.${index}`);
        });
    },
    removeEducation: (index) => {
        setDraft((prev) => markUserEdited({
            ...prev,
            education: prev.education.filter((_, i) => i !== index),
        }, "education"));
    },
    addLanguage: () => {
        setDraft((prev) => markUserEdited({
            ...prev,
            languages: [...prev.languages, { name: "", level: "" }],
        }, "languages"));
    },
    updateLanguage: (index, lang) => {
        setDraft((prev) => {
            const nextLanguages = [...prev.languages];
            nextLanguages[index] = lang;
            return markUserEdited({
                ...prev,
                languages: nextLanguages,
            }, `languages.${index}`);
        });
    },
    removeLanguage: (index) => {
        setDraft((prev) => markUserEdited({
            ...prev,
            languages: prev.languages.filter((_, i) => i !== index),
        }, "languages"));
    },
});
